import type { HTMLAttributes, ReactNode } from 'react';

import Icon, {
	type IconColor,
	type IconName,
	type IconSize,
} from 'components/Icon';
import type { HTMLTagName } from 'types';
import { cn } from 'utils/classNames';

type Size = 'base' | 'sm' | 'xs';

interface Props extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
	as: HTMLTagName;
	icon: IconName;
	iconColor?: IconColor;
	size?: Size;
	text: ReactNode;
}

const ICON_SIZES: Record<Size, IconSize> = {
	xs: 16,
	sm: 20,
	base: 24,
};
const ICON_MARGINS: Record<Size, string> = {
	xs: 'mr-1',
	sm: 'mr-1.5',
	base: 'mr-2',
};

/** Text with a leading icon aligned to the first line. */
export default function IconText({
	as: Container,
	className,
	icon,
	iconColor,
	size = 'base',
	text,
	...attrs
}: Props) {
	return (
		<Container
			{...attrs}
			className={cn(
				'flex items-start',
				size === 'xs' && 'text-xs',
				size === 'sm' && 'text-sm',
				className,
			)}
		>
			<Icon
				name={icon}
				color={iconColor}
				size={ICON_SIZES[size]}
				className={ICON_MARGINS[size]}
			/>
			{text}
		</Container>
	);
}
IconText.displayName = 'IconText';
